import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Wrapper, Column, Icon, Button, Optional } from '@honda-canada/design-system-react';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import keyCodes from '@honda-canada/js-utilities/lib/keyCodes';
import keypressCallback from '@honda-canada/js-utilities/lib/keypressCallback';
import useQuery from '../../hooks/useQuery';
import useAppName from '../../hooks/useAppName';
import useFetch from '../../hooks/useFetch';
import useConfiguration from '../../hooks/useConfiguration';
import { searchId, VIN_PARAM_FROM_URL, VALID_VIN_INPUT_LENGTH } from '../../utils/constants';
import Search from '../Search';
import Response from '../Response';
import Results from '../Results';
import { VinContextProvider } from '../../contexts/VinContext';
import componentConfig from './componentConfig';
import getHondaExperienceApiHeaders from '../../utils/getHondaExperienceApiHeaders';
import getHondaApiUrl from '../../utils/getHondaExperienceApiUrl';
import formatCampaignData from '../../utils/formatters';
import includeSpecialCharacter from '../../utils/includeSpecialCharacter';
import themeStyles from './styles/VINSearchAndResults.styles';
import pushGtmVinSearchResultEvent from '../../utils/gtm/pushGtmVinSearchResultEvent';
import validateVin from '../../utils/validateVin';
import formatVinWithSpace from '../../utils/formatVinWithSpace';
import getGtmVinSearchResult from '../../utils/gtm/getGtmVinSearchResult';
import isVinProductLineMatchSiteName from '../../utils/isVinProductLineMatchSiteName';

const Container = themeStyles.apply(Box, 'Container');
const ResultsContainer = themeStyles.apply(Wrapper, 'ResultsContainer');
const WhereToFindVinLink = themeStyles.apply(Button, 'WhereToFindVinLink');
const VinSearchColumn = themeStyles.apply(Column, 'VinSearchColumn');

const VINSearchAndResults = () => {
  const { t } = useTranslation();
  const { sitecoreContext } = useSitecoreContext();
  const language = sitecoreContext?.language || 'en';
  const config = sitecoreContext?.sotHondaExperienceApiConfig || {};
  // data fetching hook, trigger function is to be used in event handler
  const { data: vinData, error, trigger, isLoaded: isFetchingDone } = useFetch();
  // allow space or not in VIN search input
  const { allowSpace, showWhereToFindVin, asteriskColor } = useConfiguration(componentConfig);
  const params = useQuery();
  const siteName = useAppName();
  const vinNumber = params.get(VIN_PARAM_FROM_URL) || '';

  const [userVin, setUserVin] = useState(vinNumber);
  const [inputError, setInputError] = useState(false);
  const [searchedVin, setSearchedVIN] = useState('');

  const vinBrand = vinData?.campaignVinDetails?.vinDetails?.productLine;
  const brandMatchesSite = isVinProductLineMatchSiteName(vinBrand, siteName);

  /* Event handlers */
  function handleSubmit(e) {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    // validate VIN length and format
    const formattedVin = allowSpace ? formatVinWithSpace(userVin) : userVin;
    if (!validateVin(formattedVin, { allowSpace })) {
      setInputError(true);
      return;
    }
    const baseUrl = getHondaApiUrl({
      consumerName: config?.consumerName,
      env: config?.env,
    });
    // when baseUrl is empty, request url is invalid
    const url = baseUrl ? `${baseUrl}/campaigns/${formattedVin}` : '';
    const headers = getHondaExperienceApiHeaders({
      consumerId: config?.consumerId,
      consumerName: config?.consumerName,
      language,
    });

    const onFetchSuccess = data => {
      const vinProductLine = data?.campaignVinDetails?.vinDetails?.productLine;
      const vinCampaigns = data?.campaignVinDetails?.campaigns;
      // GTM search event
      const isProductLineMatch = productLine => isVinProductLineMatchSiteName(productLine, siteName);
      const searchResult = getGtmVinSearchResult({
        productLine: vinProductLine,
        campaigns: vinCampaigns,
        isProductLineMatch,
      });
      pushGtmVinSearchResultEvent(formattedVin, searchResult);
    };
    const onFetchError = () => {
      const searchResult = getGtmVinSearchResult({
        isVinValid: false,
      });
      pushGtmVinSearchResultEvent(formattedVin, searchResult);
    };
    const onFetchCompletion = () => {
      setSearchedVIN(formattedVin);
    };

    trigger({ url, headers, onSuccess: onFetchSuccess, onError: onFetchError, onCompletion: onFetchCompletion });
  }

  const resetErrorState = () => {
    setInputError(false);
  };
  const handleInputChange = e => {
    if (e.target.value.length > VALID_VIN_INPUT_LENGTH || includeSpecialCharacter(e.target.value, { allowSpace })) {
      return;
    }
    if (inputError) {
      resetErrorState();
    }
    setUserVin(e.target.value.toUpperCase());
  };
  const handleInputReset = () => {
    if (inputError) {
      resetErrorState();
    }
    setUserVin('');
  };

  /**
   * Method to read vin query param from url to perform search on first load
   */
  useEffect(() => {
    if (vinNumber) {
      handleSubmit();
    }
    // TODO: add clean up function to cancel stale fetch request
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shouldRenderResult = !error && !!vinData?.campaignVinDetails?.campaigns?.length && brandMatchesSite;
  const formattedVinData = formatCampaignData(vinData?.campaignVinDetails?.campaigns, vinBrand, t);

  const vinSearchInputProps = {
    value: userVin,
    name: 'Vin Number',
    ariaLabel: t('Sub-apps.Recalls.vinInputAria'),
    id: searchId,
    height: 'auto',
    px: 'xs',
    handleInputReset,
    onChange: handleInputChange,
  };

  return (
    <Container data-testid="cy-main-wrapper">
      <Wrapper>
        <VinSearchColumn>
          <Search
            hasError={inputError}
            handleSubmit={handleSubmit}
            onKeyDown={keypressCallback(keyCodes.ENTER, handleSubmit)}
            inputProps={vinSearchInputProps}
            adornmentProps={{
              children: <Icon name="search" />,
            }}
            dataTestId="cy-search-bar"
            asteriskColor={asteriskColor}
          />
          <Optional when={showWhereToFindVin}>
            <WhereToFindVinLink
              styling="tertiary"
              as="a"
              href="#where-to-find-your-vin"
              aria-label={t('Sub-apps.Recalls.whereToFindMyVinAria')}
            >
              {t('Sub-apps.Recalls.whereToFindMyVinButton')}
            </WhereToFindVinLink>
          </Optional>
        </VinSearchColumn>
        <Column mb={['l', 'xl']}>
          <Optional when={isFetchingDone}>
            <Response
              vinData={vinData?.campaignVinDetails}
              userVin={searchedVin}
              brandMatchesSite={brandMatchesSite}
              isVinValid={!error}
            />
          </Optional>
        </Column>
      </Wrapper>
      <Optional when={shouldRenderResult}>
        <VinContextProvider vin={searchedVin}>
          <ResultsContainer data-testid="cy-recall-notices" brandName={vinBrand}>
            <Results formattedData={formattedVinData} />
          </ResultsContainer>
        </VinContextProvider>
      </Optional>
    </Container>
  );
};

export default VINSearchAndResults;
